import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule, type RoutineView } from '../../types';
import type { WarningFinding } from '../../util/errors';
import {
  validateConditional,
  validateConditionalIsComplete,
} from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class IfRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.If', 'If');
  }

  protected validate({ args, routine, warn }: CanonicalValidateArgs): void {
    if (!this.hasConditions(args)) return;
    warn(this.validateConditions(args, routine));
  }

  /**
   * Check if the step has conditions.
   * @param args - The arguments of the step.
   * @returns True if the step has conditions, false otherwise.
   */
  private hasConditions(args: RcStep.Arguments): boolean {
    return (args as any).condition != null;
  }

  /**
   * Validate parsed conditions against routine.
   * Throws if the condition references invalid data.
   *
   * In the remote-control UI schema, `condition` is a JSON string
   * (Firestore doesn't support nested arrays). In the routine-runner
   * schema, `condition` is already a parsed object.
   *
   * @throws {ActionRequiredError}
   */
  private validateConditions(
    args: RcStep.Arguments,
    routine: RoutineView,
  ): WarningFinding | undefined {
    const raw = (args as any).condition;

    const parsed = typeof raw === 'string' ? JSON.parse(raw || '[]') : raw;

    // `validateConditional` expects Conditional[][] – a non-array value
    // (e.g. a single object) would silently bypass the for-loops inside.
    // Guard against that by treating non-array values as empty.
    const conditions = Array.isArray(parsed) ? parsed : [];

    // An incomplete condition is already caught downstream, by the runner-schema
    // cross-check failing to convert it - but only as a type mismatch on the
    // raw JSON string. Assert it here so the operator gets the real reason, and
    // so `If` and `ElseIf` reject the same configurations for the same stated
    // cause.
    validateConditionalIsComplete(conditions);

    return validateConditional(conditions, routine);
  }
}

export const IfValidation: ValidationRule = new IfRule().toValidationRule();
